import React from 'react';
import { Doughnut } from 'react-chartjs-2';

const SpendingBehavior = () => {
 
  const defaultData = {
    title: "Spending Behavior",
    chartSection: {
      title: "Noticed a Change in Dog Food\nSpending (Past 12 Months)",
      yesPercentage: 57.5,
      noPercentage: 42.5,
      yesLabel: "YES",
      noLabel: "NO"
    },
    strategicMeaning: {
      title: "Strategic meaning for the category",
      points: [
        {
          icon: "✓",
          text: "Premiumization continues but many owners are trading to (private labels, club stores, turbos, influencer-followers) remain stable."
        },
        {
          icon: "✓",
          text: "Value-sensitive groups need reassurance, promotions, and confidence their pet is happy."
        },
        {
          icon: "✓",
          text: "Rising costs are reshaping expectations, but not reducing interest in better quality foods."
        }
      ]
    },
    insights: [
      {
        title: "Majority of owners are facing rising dog food costs",
        description: "More than half of dog owners (57.5%) report that their monthly spending has increased in the past year. This closely agrees broadly inflation in the category, driven by higher ingredient and operational costs.",
        // bgColor: "bg-[#aee772]",
        borderColor: "border-lime-400"
      },
      {
        title: "The increase is most intense for vulnerable segments",
        description: "Certain groups feel cost pressure far more strongly:",
        bulletPoints: [
          "Lower-income households",
          "Multi-dog households",
          "Owners of dogs with pre-existing conditions"
        ],
        // bgColor: "bg-[#aee772]",
        borderColor: "border-lime-400"
      },
      {
        title: "Spending comfort is healthy, but hides pressure underneath",
        description: "Many feel \"comfortable\" with their spending, but costs still rise. Unbalanced rollbacks and premium consumers may in code six exposers as they trade differently across the portfolio.",
        // bgColor: "bg-[#aee772]",
        borderColor: "border-lime-400"
      },
      {
        title: "Cost-sensitive households are feeling strained",
        description: "Those not comfortable with their spending are significantly more likely to report trading down, downsizing, or switching to cheaper alternatives. This segment signals risk for trade-down behavior and brand switching.",
        // bgColor: "bg-[#aee772]",
        borderColor: "border-lime-400"
      }
    ]
  };

  const componentData =  defaultData;
  const donutData = {
    labels: ['YES', 'NO'],
    datasets: [{
      data: [defaultData.chartSection.yesPercentage, defaultData.chartSection.noPercentage],
      backgroundColor: ['#aee772', '#D1D5DB'],
      borderWidth: 0,
      cutout: '70%'
    }]
  };

    const donutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
        legend: {
            display: false
        },
        tooltip: {
            callbacks: {
            label: function(context:any) {
                return context.label + ': ' + context.parsed + '%';
            }
            }
        }
        }
    };

  return (
     <div className="flex flex-col h-full items-center w-full">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">{componentData.title}</h1>
     

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Left Column - Chart and Strategic Meaning */}
        <div className="space-y-2">
          {/* Chart Section */}
          <div className="bg-white p-2">
            <h2 className="text-center text-lg font-semibold mb-4 whitespace-pre-line">
              {componentData.chartSection.title}
            </h2>
            
            {/* Donut Chart */}
            <div className="relative flex justify-center items-center mb-4">
               <Doughnut data={donutData} options={donutOptions} />
              
              {/* Labels */}
              <div className="absolute left-8 top-1/2 transform -translate-y-1/2 text-right">
                <div className="text-xs font-semibold text-gray-900">{componentData.chartSection.noLabel}</div>
                <div className="text-xs font-semibold text-gray-900">{componentData.chartSection.noPercentage}%</div>
              </div>
              
              <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                <div className="text-xs font-semibold text-gray-900">{componentData.chartSection.yesLabel}</div>
                <div className="text-xs font-semibold text-green-900">{componentData.chartSection.yesPercentage}%</div>
              </div>
            </div>
          </div>

          {/* Strategic Meaning Section */}
          <div className="bg-white p-2 rounded-lg shadow-sm border-2 border-lime-400">
            <h3 className="text-lg font-bold mb-4 text-gray-900">
              {componentData.strategicMeaning.title}
            </h3>
            <div className="space-y-3">
              {componentData.strategicMeaning.points.map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <span className="text-green-500 font-bold text-xl mt-0.5">{point.icon}</span>
                  <p className="text-sm text-gray-700 leading-relaxed">{point.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Insights */}
        <div className="space-y-2">
          {componentData.insights.map((insight, index) => (
            <div
              key={index}
              className={` border-2 ${insight.borderColor} p-2 rounded-xl`}
            >
              <h3 className="font-bold text-gray-900 mb-1 text-base">
                {insight.title}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-1">
                {insight.description}
              </p>
              {insight.bulletPoints && (
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {insight.bulletPoints.map((point, idx) => (
                    <li key={idx} className="text-sm text-gray-700">{point}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpendingBehavior;